import { Fragment, useRef, useEffect, useState, type ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ProspectResponse } from "../../../../types/api";
import type { DefaultTagDefinition } from "../../../../types/tags";
import "./PlayersTable.css";
import { StarIcon } from "../../../../assets/StarIcon";
import { NoteIcon } from "../../../../assets/NoteIcon";
import { VideoIcon } from "../../../../assets/VideoIcon";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { buildProspectLink } from "../../../../utils/prospectLinks";
import { describeProspectClass } from "../../../../utils/prospect-class-display";
import type { ProspectSection } from "./ProspectMainViewer";
import { SortableHeaderCell } from "../../../../components/SortableHeaderCell";
import type { ProspectSortColumn, TableSort } from "../../../../types/sort";
import {
  COL_ACTIONS,
  COL_DRAG,
  COL_GRAD_YEAR,
  COL_JERSEY,
  COL_SCHOOL,
  COL_NAME,
  COL_POSITION,
  COL_SELECT,
  buildPlayersColumns,
  type PlayersColumn,
  type PlayersColumnLayout,
} from "./playersTableColumns";
import { tourAnchor, type TourAnchorId } from "../../../tours/tourAnchors";

/** Tags an element as a tour anchor on the first row only; later rows get nothing. */
const rowAnchor = (
  index: number,
  id: TourAnchorId
): { "data-tour-anchor"?: TourAnchorId } => (index === 0 ? tourAnchor(id) : {});

export interface PlayersTableProps {
  prospects: ProspectResponse[];
  tagDefinitions: DefaultTagDefinition[];
  loading: boolean;
  onProspectClick?: (
    prospectId: string,
    section?: ProspectSection
  ) => void;
  onFavoriteToggle: (prospectId: string, currentIsFavorite: boolean) => void;
  onTagClick: (prospectId: string, tagName: string) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (id: string, checked: boolean) => void;
  isReorderMode?: boolean;
  onReorder?: (newOrder: ProspectResponse[]) => void;
  /**
   * Current sort, and the handler for a header click. Required together — a live
   * sort UI with no `sort` to read would render clickable headers that never show
   * which column is active.
   */
  sort: TableSort;
  onSortChange: (column: ProspectSortColumn) => void;
  /**
   * The viewer's column arrangement. Omitted means the default column set, which
   * is what every caller rendered before columns became configurable.
   */
  columnLayout?: PlayersColumnLayout;
}

const getJerseyNumber = (
  prospect: ProspectResponse,
  selectedTeamId: string | null
): string => {
  if (!prospect.teams || prospect.teams.length === 0) {
    return "-";
  }

  if (selectedTeamId) {
    const selectedTeam = prospect.teams.find((t) => t.id === selectedTeamId);
    if (selectedTeam) {
      if (
        selectedTeam.jerseyNumber === null ||
        selectedTeam.jerseyNumber === undefined
      ) {
        return "-";
      }
      return String(selectedTeam.jerseyNumber);
    }
    return "-";
  }

  const primaryTeam = prospect.teams[0];
  if (
    primaryTeam.jerseyNumber === null ||
    primaryTeam.jerseyNumber === undefined
  ) {
    return "-";
  }
  return String(primaryTeam.jerseyNumber);
};

const getTagValues = (
  prospect: ProspectResponse,
  tagName: string,
  validOptions: string[]
): string => {
  if (!prospect.defaultTags || prospect.defaultTags.length === 0) return "-";
  const tagValues = prospect.defaultTags
    .filter((tag) => tag.tagName === tagName && validOptions.includes(tag.tagValue))
    .map((tag) => tag.tagValue);
  return tagValues.length > 0 ? tagValues.join(", ") : "-";
};

// Six-dot drag handle icon
const DragHandleIcon = () => (
  <svg
    className="players-table__drag-handle-icon"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="5" cy="4" r="1.2" />
    <circle cx="11" cy="4" r="1.2" />
    <circle cx="5" cy="8" r="1.2" />
    <circle cx="11" cy="8" r="1.2" />
    <circle cx="5" cy="12" r="1.2" />
    <circle cx="11" cy="12" r="1.2" />
  </svg>
);

/**
 * Video icon with a count badge, mirroring the note button next to it. Rendered
 * in both the sortable (reorder-mode) and static row bodies, so it lives here
 * rather than being inlined twice.
 */
const VideoCountButton = ({
  prospect,
  onOpenVideos,
  anchorProps,
}: {
  prospect: ProspectResponse;
  onOpenVideos?: (prospectId: string, section?: ProspectSection) => void;
  /** Tour anchor, passed in rather than hard-coded so only the first row carries it. */
  anchorProps?: { "data-tour-anchor"?: TourAnchorId };
}) => {
  const count = prospect.videoCount ?? 0;
  const suffix = count > 0 ? ` (${count})` : "";
  return (
    <button
      className="players-table__video-button"
      data-testid={`video-count-${prospect.id}`}
      {...anchorProps}
      onClick={(e) => {
        e.stopPropagation();
        onOpenVideos?.(prospect.id, "videos");
      }}
      title={`View videos${suffix}`}
      aria-label={`View videos for ${prospect.firstName} ${prospect.lastName}${suffix}`}
    >
      <VideoIcon className="players-table__video-icon" />
      {count > 0 ? (
        <span className="players-table__video-badge" aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </button>
  );
};

/**
 * The data cells for one prospect, emitted in the table's column order.
 *
 * Both row bodies (reorder-mode and static) render through this, so a row can
 * never disagree with the header about which column it is filling. The two
 * bodies differ only in their drag and select cells, which they pass in as
 * `slots` — everything else is identical between them.
 */
const PlayerRowCells = ({
  prospect,
  columns,
  rowIndex,
  selectedTeamId,
  searchParams,
  onProspectClick,
  onFavoriteToggle,
  onTagClick,
  slots,
}: {
  prospect: ProspectResponse;
  columns: PlayersColumn[];
  /** Position in the body — only row 0 carries the tour anchors. */
  rowIndex: number;
  selectedTeamId: string | null;
  searchParams: URLSearchParams;
  onProspectClick?: (prospectId: string, section?: ProspectSection) => void;
  onFavoriteToggle: (prospectId: string, currentIsFavorite: boolean) => void;
  onTagClick: (prospectId: string, tagName: string) => void;
  slots: { drag: ReactNode; select: ReactNode };
}) => {
  const navigate = useNavigate();
  const classDisplay = describeProspectClass(prospect);
  // Every remaining column is a tag category, and the tour points at one cell, not all of them.
  const firstTagColumnKey = columns.find((column) => column.tagName)?.key;

  return (
    <>
      {columns.map((column) => {
        switch (column.key) {
          case COL_DRAG:
            return <Fragment key={column.key}>{slots.drag}</Fragment>;

          case COL_SELECT:
            return <Fragment key={column.key}>{slots.select}</Fragment>;

          case COL_NAME:
            return (
              <td
                key={column.key}
                className="players-table__cell players-table__cell--name"
              >
                <Link
                  to={buildProspectLink(prospect.id, searchParams)}
                  className="players-table__row-link"
                  {...rowAnchor(rowIndex, "players.row.open")}
                  aria-label={`View details for ${prospect.firstName} ${prospect.lastName}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onProspectClick?.(prospect.id);
                  }}
                >
                  {prospect.lastName}, {prospect.firstName}
                </Link>
              </td>
            );

          case COL_POSITION:
            return (
              <td key={column.key} className="players-table__cell">
                {prospect.position || "-"}
              </td>
            );

          case COL_GRAD_YEAR:
            return (
              // One cell, two ladders: a JUCO player reads "JC 2" where everyone
              // else reads a graduation year. `describeProspectClass` owns the
              // choice (and the `playerLevel = 0` falsiness trap).
              //
              // COLUMN HEADER: renamed "Grad Year" → "Class". The column is
              // hideable with a per-org localStorage layout, so a coach who hid
              // "Grad Year" would see the JC year nowhere — and a header reading
              // "Grad Year" over a cell reading "JC 2" is simply wrong. "Class"
              // covers both facts (a graduation year names a class; so does a JC
              // year), and it is already what mobile's iPad table calls this
              // column, so the two platforms now agree. Hiding the column is
              // still an explicit opt-out of class information altogether, which
              // is the same trade-off it always was.
              <td
                key={column.key}
                className="players-table__cell"
                // `2027` alone does not say whether the player is in high school
                // or the transfer portal, and "JC 2" is announced as loose
                // characters. `aria-label` replaces what assistive technology
                // reads for this cell without touching the visible text; the
                // `title` gives a sighted user the same fact on hover. A
                // visually-hidden span would have done it too, but it would also
                // land in the cell's textContent, which the column tests read.
                aria-label={classDisplay.accessibilityLabel}
                title={classDisplay.levelLabel ?? undefined}
              >
                {classDisplay.text}
              </td>
            );

          case COL_JERSEY:
            return (
              <td key={column.key} className="players-table__cell">
                {getJerseyNumber(prospect, selectedTeamId) !== "-" ? (
                  <span className="players-table__jersey-pill">
                    #{getJerseyNumber(prospect, selectedTeamId)}
                  </span>
                ) : (
                  "-"
                )}
              </td>
            );

          case COL_SCHOOL:
            return (
              // `school` is optional: an API older than this column omits it.
              <td key={column.key} className="players-table__cell">
                {prospect.school ?? ""}
              </td>
            );

          case COL_ACTIONS:
            return (
              <td
                key={column.key}
                className="players-table__cell players-table__cell--actions"
              >
                <button
                  className="players-table__favorite-button"
                  {...rowAnchor(rowIndex, "players.row.favorite")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavoriteToggle(prospect.id, prospect.isFavorite);
                  }}
                  title={
                    prospect.isFavorite
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                >
                  <StarIcon
                    className={`players-table__favorite ${
                      prospect.isFavorite
                        ? "players-table__favorite--filled"
                        : "players-table__favorite--outline"
                    }`}
                    filled={prospect.isFavorite}
                    size={24}
                  />
                </button>
                <VideoCountButton
                  prospect={prospect}
                  onOpenVideos={onProspectClick}
                  anchorProps={rowAnchor(rowIndex, "players.row.video")}
                />
                <button
                  className="players-table__note-button"
                  {...rowAnchor(rowIndex, "players.row.note")}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/recruiting-notes/new?prospectId=${prospect.id}`);
                  }}
                  title={
                    prospect.noteCount && prospect.noteCount > 0
                      ? `Add note (${prospect.noteCount} existing)`
                      : "Add note"
                  }
                >
                  <NoteIcon className="players-table__note-icon" />
                  {prospect.noteCount && prospect.noteCount > 0 ? (
                    <span
                      className="players-table__note-badge"
                      aria-hidden="true"
                    >
                      {prospect.noteCount > 99 ? "99+" : prospect.noteCount}
                    </span>
                  ) : null}
                </button>
              </td>
            );

          default: {
            // Every remaining column is a tag category.
            const tagName = column.tagName;
            if (!tagName) return null;
            const value = getTagValues(prospect, tagName, column.options ?? []);
            return (
              <td
                key={column.key}
                className="players-table__cell players-table__cell--tag"
                {...(column.key === firstTagColumnKey
                  ? rowAnchor(rowIndex, "players.row.tag")
                  : {})}
                onClick={(e) => {
                  e.stopPropagation();
                  onTagClick(prospect.id, tagName);
                }}
              >
                <span className="players-table__tag-text" title={value}>
                  {value}
                </span>
              </td>
            );
          }
        }
      })}
    </>
  );
};

interface SortableRowProps {
  /** Position in the body. Only row 0 carries the tour anchors. */
  rowIndex: number;
  prospect: ProspectResponse;
  columns: PlayersColumn[];
  selectedTeamId: string | null;
  onProspectClick?: (
    prospectId: string,
    section?: ProspectSection
  ) => void;
  onFavoriteToggle: (prospectId: string, currentIsFavorite: boolean) => void;
  onTagClick: (prospectId: string, tagName: string) => void;
  searchParams: URLSearchParams;
}

const SortableRow = ({
  prospect,
  columns,
  rowIndex,
  selectedTeamId,
  onProspectClick,
  onFavoriteToggle,
  onTagClick,
  searchParams,
}: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: prospect.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? ("relative" as const) : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`players-table__row${
        isDragging ? " players-table__row--dragging" : ""
      }`}
    >
      <PlayerRowCells
        prospect={prospect}
        rowIndex={rowIndex}
        columns={columns}
        selectedTeamId={selectedTeamId}
        searchParams={searchParams}
        onProspectClick={onProspectClick}
        onFavoriteToggle={onFavoriteToggle}
        onTagClick={onTagClick}
        slots={{
          drag: (
            <td className="players-table__cell players-table__cell--drag">
              <button
                className="players-table__drag-handle"
                {...attributes}
                {...listeners}
                aria-label={`Drag to reorder ${prospect.firstName} ${prospect.lastName}`}
              >
                <DragHandleIcon />
              </button>
            </td>
          ),
          select: (
            <td className="players-table__cell players-table__cell--checkbox" />
          ),
        }}
      />
    </tr>
  );
};

export const PlayersTable = ({
  prospects,
  tagDefinitions,
  loading,
  onProspectClick,
  onFavoriteToggle,
  onTagClick,
  selectedIds,
  onSelectionChange,
  isReorderMode = false,
  onReorder,
  sort,
  onSortChange,
  columnLayout,
}: PlayersTableProps) => {
  const [searchParams] = useSearchParams();
  const selectedTeamId = searchParams.get("teamId");

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const allPageSelected =
    prospects.length > 0 && prospects.every((p) => selectedIds?.has(p.id));
  const somePageSelected =
    !allPageSelected && prospects.some((p) => selectedIds?.has(p.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = somePageSelected;
    }
  }, [somePageSelected]);

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    prospects.forEach((p) => onSelectionChange?.(p.id, e.target.checked));
  };

  const [isDraggingAtBottom, setIsDraggingAtBottom] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    const lastId = prospects[prospects.length - 1]?.id;
    setIsDraggingAtBottom(!!over && over.id === lastId);
  };

  // Reorder mode owns row order — it writes the manual order the user is
  // dragging — so a sort click there would reorder the rows out from under the
  // drag. Headers stay visible and keep their aria-sort, they just do nothing.
  const isSortable = !isReorderMode;

  // The one ordered column list the header and both row bodies render from.
  const columns = buildPlayersColumns(tagDefinitions, columnLayout);

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDraggingAtBottom(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = prospects.findIndex((p) => p.id === active.id);
    const newIndex = prospects.findIndex((p) => p.id === over.id);
    onReorder?.(arrayMove(prospects, oldIndex, newIndex));
  };

  const header = (
    <thead className="players-table__header">
      <tr>
        {columns.map((column) => {
          if (column.key === COL_DRAG) {
            return (
              <th
                key={column.key}
                className="players-table__header-cell players-table__header-cell--drag"
              />
            );
          }

          if (column.key === COL_SELECT) {
            return (
              <th
                key={column.key}
                className="players-table__header-cell players-table__header-cell--checkbox"
              >
                {!isReorderMode && (
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="players-table__checkbox"
                    checked={allPageSelected}
                    onChange={handleSelectAllChange}
                    aria-label="Select all on this page"
                  />
                )}
              </th>
            );
          }

          if (column.key === COL_ACTIONS) {
            return (
              <th key={column.key} className="players-table__header-cell"></th>
            );
          }

          // School has no server sort, so it gets a plain heading.
          if (!column.sortColumn) {
            return (
              <th key={column.key} className="players-table__header-cell">
                {column.label}
              </th>
            );
          }

          // Every other data column sorts.
          return (
            <SortableHeaderCell
              key={column.key}
              column={column.sortColumn!}
              label={column.label}
              sort={sort}
              onSort={onSortChange}
              sortable={isSortable}
              className="players-table__header-cell"
            />
          );
        })}
      </tr>
    </thead>
  );

  // Refetching with rows already on screen (sorting, paging, filtering) keeps
  // those rows rendered and just dims them.
  //
  // Swapping them for a spinner is what made sorting flicker: the table is
  // `table-layout: auto`, so column widths come from cell content. With only a
  // single spanning cell in the body, every column collapsed to the width of its
  // header label and the table lost its height, then both snapped back when the
  // rows arrived. Holding the previous rows means nothing is re-measured, so
  // there is no reflow to see — and the header the user just clicked keeps its
  // position and keyboard focus.
  const isRefreshing = loading && prospects.length > 0;

  // Nothing to hold on to — first load of the section, or of a new team.
  if (loading && prospects.length === 0) {
    return (
      <div
        ref={tableContainerRef}
        className="players-table-container"
        {...tourAnchor("players.list")}
      >
        <table className="players-table" aria-busy>
          {header}
          <tbody className="players-table__body">
            <tr>
              <td
                className="players-table__cell players-table__cell--loading"
                colSpan={columns.length}
              >
                <div className="players-table-spinner"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (prospects.length === 0) {
    // The header stays, for the same reason it stays while loading: sorting into
    // a filter combination with no results would otherwise unmount the button
    // the user just pressed, drop keyboard focus to the document body, and leave
    // no way to undo the sort short of editing the URL. Keeping the table shell
    // also means the empty state is not a differently-sized box swapped in.
    return (
      <div
        ref={tableContainerRef}
        className="players-table-container"
        {...tourAnchor("players.list")}
      >
        <table className="players-table">
          {header}
          <tbody className="players-table__body">
            <tr>
              <td
                className="players-table__cell players-table__cell--empty"
                colSpan={columns.length}
              >
                No players found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      ref={tableContainerRef}
      {...tourAnchor("players.list")}
      className={`players-table-container${
        isReorderMode ? " players-table-container--reorder" : ""
      }`}
    >
      <table
        className={`players-table${
          isRefreshing ? " players-table--refreshing" : ""
        }`}
        aria-busy={isRefreshing}
      >
        {header}
        {isReorderMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            autoScroll={isDraggingAtBottom ? { enabled: false } : true}
          >
            <SortableContext
              items={prospects.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody className="players-table__body">
                {prospects.map((prospect, index) => (
                  <SortableRow
                    key={prospect.id}
                    prospect={prospect}
                    rowIndex={index}
                    columns={columns}
                    selectedTeamId={selectedTeamId}
                    onProspectClick={onProspectClick}
                    onFavoriteToggle={onFavoriteToggle}
                    onTagClick={onTagClick}
                    searchParams={searchParams}
                  />
                ))}
              </tbody>
            </SortableContext>
          </DndContext>
        ) : (
          <tbody className="players-table__body">
            {prospects.map((prospect, index) => (
              <tr
                key={prospect.id}
                className={`players-table__row${
                  selectedIds?.has(prospect.id)
                    ? " players-table__row--selected"
                    : ""
                }`}
              >
                <PlayerRowCells
                  prospect={prospect}
                  rowIndex={index}
                  columns={columns}
                  selectedTeamId={selectedTeamId}
                  searchParams={searchParams}
                  onProspectClick={onProspectClick}
                  onFavoriteToggle={onFavoriteToggle}
                  onTagClick={onTagClick}
                  slots={{
                    drag: (
                      <td className="players-table__cell players-table__cell--drag" />
                    ),
                    select: (
                      <td className="players-table__cell players-table__cell--checkbox">
                        <input
                          type="checkbox"
                          className="players-table__checkbox"
                          {...rowAnchor(index, "players.row.select")}
                          checked={selectedIds?.has(prospect.id) ?? false}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectionChange?.(prospect.id, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${prospect.firstName} ${prospect.lastName}`}
                        />
                      </td>
                    ),
                  }}
                />
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};
